import type { Change } from "diff";
import { useTranslation } from "react-i18next";
import { cx } from "../cx";

const keyFor = (part: Change, offset: number) =>
  `${offset}-${part.added ? "ins" : part.removed ? "del" : "same"}`;

/**
 * Word-level differences from jsdiff (`diffWords`) inside book text: removed
 * runs on the del colour, inserted runs on the ins colour, the rest plain.
 * `side` hides the parts that do not belong on this column of Review.
 */
export function DiffText({
  parts,
  side = "both",
  className = "",
}: {
  parts: ReadonlyArray<Change>;
  /** `manuscript` shows unchanged and removed, `script` unchanged and inserted. */
  side?: "both" | "manuscript" | "script";
  className?: string;
}) {
  const { t } = useTranslation("components");
  let offset = 0;
  return (
    <span data-component="DiffText" className={cx("font-serif text-book text-ink", className)}>
      {parts.map((part) => {
        const key = keyFor(part, offset);
        offset += part.value.length;
        if (part.removed) {
          if (side === "script") return null;
          return (
            <del key={key} className="bg-diff-del no-underline">
              <span className="sr-only">{t("diff.removed")}: </span>
              {part.value}
            </del>
          );
        }
        if (part.added) {
          if (side === "manuscript") return null;
          return (
            <ins key={key} className="bg-diff-ins no-underline">
              <span className="sr-only">{t("diff.inserted")}: </span>
              {part.value}
            </ins>
          );
        }
        return <span key={key}>{part.value}</span>;
      })}
    </span>
  );
}
